import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getAccessToken } from "../utils/auth";
import { API_BASE_URL } from "../utils/api";
import { compareSwimmerNamesByLastName } from "../utils/swimmerSort";
import MatchupDetailsModal from "./MatchupDetailsModal";

const API = API_BASE_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${getAccessToken()}`,
});

// ── Shared helpers ────────────────────────────────────────────────────────────

const StatusMessage = ({ msg }) => {
  if (!msg) return null;
  const isError = msg.startsWith("Error");
  return (
    <p
      style={{
        marginTop: "0.75rem",
        color: isError ? "#ff93b0" : "#a8fff0",
        fontWeight: "bold",
      }}
    >
      {msg}
    </p>
  );
};

// ── Upload Files Tab ──────────────────────────────────────────────────────────

const UploadTab = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [sd3File, setSd3File] = useState(null);
  const [csvStatus, setCsvStatus] = useState("");
  const [sd3Status, setSd3Status] = useState("");
  const [meetStatus, setMeetStatus] = useState("");
  const [selectedMeetIds, setSelectedMeetIds] = useState([]);
  const [deletingMeets, setDeletingMeets] = useState(false);
  const [statusSummary, setStatusSummary] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const uploadedMeets = Array.isArray(statusSummary?.uploaded_meets)
    ? statusSummary.uploaded_meets
    : [];

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await axios.get(`${API}/admin/status`, {
        headers: authHeaders(),
      });
      setStatusSummary(res.data);
    } catch (e) {
      setStatusSummary(null);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const uploadCsv = async () => {
    if (!csvFile) return;
    setUploading(true);
    setCsvStatus("");
    const form = new FormData();
    form.append("file", csvFile);
    try {
      const res = await axios.post(`${API}/admin/upload-csv`, form, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      if (res.data?.error) {
        setCsvStatus(`Error: ${res.data.error}`);
        return;
      }
      const addedSwimmers = Array.isArray(res.data.added_swimmers)
        ? res.data.added_swimmers
        : [];
      const warningText = res.data.warning ? ` ${res.data.warning}` : "";
      const addedPreview =
        addedSwimmers.length > 0
          ? ` Added this upload (${addedSwimmers.length}): ${addedSwimmers.slice(0, 8).join(", ")}${addedSwimmers.length > 8 ? "…" : ""}`
          : "";
      setCsvStatus(
        (res.data.message || res.data.error || "Uploaded.") +
          warningText +
          addedPreview,
      );
      fetchStatus();
    } catch (e) {
      setCsvStatus("Error: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setUploading(false);
    }
  };

  const uploadSd3 = async () => {
    if (!sd3File) return;
    setUploading(true);
    setSd3Status("");
    const form = new FormData();
    form.append("file", sd3File);
    try {
      const res = await axios.post(`${API}/admin/upload-sd3`, form, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      const summary = res.data.summary
        ? ` Results: ${res.data.summary.results_count}, DQs: ${res.data.summary.disqualified_count}.`
        : "";
      setSd3Status(
        (res.data.message || res.data.error || "Uploaded.") + summary,
      );
      fetchStatus();
    } catch (e) {
      setSd3Status("Error: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setUploading(false);
    }
  };

  const resetMeetHistory = async () => {
    if (
      !window.confirm(
        "Clear all uploaded meet results and result overrides? This keeps roster and coach assignments intact.",
      )
    ) {
      return;
    }

    setUploading(true);
    setSd3Status("");
    try {
      const res = await axios.post(
        `${API}/admin/reset-meet-history`,
        {},
        { headers: authHeaders() },
      );
      setSd3Status(res.data.message || "Meet history cleared.");
      fetchStatus();
    } catch (e) {
      setSd3Status("Error: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setUploading(false);
    }
  };

  const toggleSelectedMeet = (meetId) => {
    if (!meetId) return;
    setSelectedMeetIds((prev) =>
      prev.includes(meetId)
        ? prev.filter((id) => id !== meetId)
        : [...prev, meetId],
    );
  };

  const deleteSelectedMeets = async () => {
    if (!selectedMeetIds.length) return;
    if (
      !window.confirm(
        `Delete ${selectedMeetIds.length} selected meet(s) and all associated results? This cannot be undone.`,
      )
    ) {
      return;
    }

    setDeletingMeets(true);
    setMeetStatus("");
    try {
      for (const meetId of selectedMeetIds) {
        await axios.delete(`${API}/admin/meets/${encodeURIComponent(meetId)}`, {
          headers: authHeaders(),
        });
      }
      setMeetStatus(`${selectedMeetIds.length} meet(s) deleted successfully.`);
      setSelectedMeetIds([]);
      fetchStatus();
    } catch (e) {
      setMeetStatus("Error: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setDeletingMeets(false);
    }
  };

  return (
    <div>
      <h2 style={sectionTitle}>Upload Files</h2>

      <div style={{ ...card, marginBottom: "1.5rem" }}>
        <div style={statusHeader}>
          <div>
            <h3 style={cardTitle}>Current Data Status</h3>
            <p style={hint}>
              This shows what the backend currently has loaded and cached.
            </p>
          </div>
          <button type="button" onClick={fetchStatus} style={secondaryBtn}>
            Refresh Status
          </button>
        </div>

        {statusLoading ? (
          <p>Loading backend status…</p>
        ) : !statusSummary ? (
          <p>Unable to load backend status.</p>
        ) : (
          <div style={statusGrid}>
            <div style={statusCard}>
              <strong>
                {statusSummary.roster_entries_loaded ??
                  statusSummary.baseline_times_loaded ??
                  0}
              </strong>
              <span>Roster Entries</span>
            </div>
            <div style={statusCard}>
              <strong>{statusSummary.results_loaded}</strong>
              <span>Meet Results</span>
            </div>
            <div style={statusCard}>
              <strong>{statusSummary.meets_loaded ?? 0}</strong>
              <span>Meets Loaded</span>
            </div>
            <div style={statusCard}>
              <strong>
                {statusSummary.game_locked ? "Locked" : "Unlocked"}
              </strong>
              <span>Game Status</span>
            </div>
            <div style={statusCard}>
              <strong style={{ color: statusSummary.season?.active ? "#00f5d4" : "#ff93b0" }}>
                {statusSummary.season?.active
                  ? `Active (${statusSummary.season.season_year})`
                  : statusSummary.season?.started_at
                  ? `Ended (${statusSummary.season.season_year})`
                  : "Not Started"}
              </strong>
              <span>Season</span>
            </div>
            <div style={statusCard}>
              <strong>{statusSummary.swimmers_loaded}</strong>
              <span>Swimmers Parsed</span>
            </div>
            <div style={statusCard}>
              <strong>{statusSummary.events_loaded}</strong>
              <span>Events Parsed</span>
            </div>
            <div style={statusCard}>
              <strong>
                {statusSummary.result_summary?.baseline_matched_count ?? 0}
              </strong>
              <span>Baseline Matches</span>
            </div>
            <div style={statusCard}>
              <strong>
                {statusSummary.result_summary?.disqualified_count ?? 0}
              </strong>
              <span>DQs</span>
            </div>
          </div>
        )}
      </div>

      <div style={card}>
        <h3 style={cardTitle}>1 · Team Roster CSV</h3>
        <p style={hint}>
          Upload this first to set the current swimmer roster and coach
          assignments.
        </p>
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setCsvFile(e.target.files[0])}
            style={fileInput}
          />
          <button
            onClick={uploadCsv}
            disabled={!csvFile || uploading}
            style={btn}
          >
            {uploading ? "Uploading…" : "Upload CSV"}
          </button>
        </div>
        <StatusMessage msg={csvStatus} />
      </div>

      <div style={{ ...card, marginTop: "1.5rem" }}>
        <h3 style={cardTitle}>2 · Swim Meet Results (.sd3)</h3>
        <p style={hint}>
          Upload the SD3 results file — scores will be recalculated
          automatically.
        </p>
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="file"
            accept=".sd3"
            onChange={(e) => setSd3File(e.target.files[0])}
            style={fileInput}
          />
          <button
            onClick={uploadSd3}
            disabled={!sd3File || uploading}
            style={btn}
          >
            {uploading ? "Uploading…" : "Upload SD3"}
          </button>
          <button
            type="button"
            onClick={resetMeetHistory}
            disabled={uploading}
            style={{ ...btn, ...resetBtn }}
          >
            Reset Meet History
          </button>
        </div>
        <StatusMessage msg={sd3Status} />
        <div style={uploadedMeetsWrap}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.8rem",
              flexWrap: "wrap",
            }}
          >
            <h4 style={uploadedMeetsTitle}>Uploaded Meets</h4>
            <button
              type="button"
              onClick={deleteSelectedMeets}
              disabled={
                !selectedMeetIds.length ||
                uploading ||
                deletingMeets ||
                statusLoading
              }
              style={{
                ...btn,
                ...resetBtn,
                fontSize: "0.82rem",
                marginBottom: "0.6rem",
              }}
            >
              Delete Selected ({selectedMeetIds.length})
            </button>
          </div>
          {uploadedMeets.length > 0 ? (
            <div style={uploadedMeetsList}>
              {uploadedMeets.map((meet) => {
                const dateText = (meet?.meet_date || "Unknown date").trim();
                const titleText = (meet?.meet_title || "Meet").trim();
                const key =
                  meet?.meet_id || `${meet?.source_file || "meet"}-${dateText}`;
                const selected = meet?.meet_id
                  ? selectedMeetIds.includes(meet.meet_id)
                  : false;

                return (
                  <div key={key} style={uploadedMeetRow}>
                    <label style={uploadedMeetCheckboxLabel}>
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={!meet?.meet_id || uploading || deletingMeets}
                        onChange={() => toggleSelectedMeet(meet?.meet_id)}
                        style={uploadedMeetCheckbox}
                      />
                      <span style={uploadedMeetCheckboxText}>
                        {selected ? "✔" : "○"}
                      </span>
                    </label>
                    <span style={uploadedMeetDate}>{dateText}</span>
                    <span style={uploadedMeetTitle}>{titleText}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={uploadedMeetEmpty}>No meets uploaded yet.</p>
          )}
          <StatusMessage msg={meetStatus} />
        </div>
      </div>
    </div>
  );
};

// ── Roster Tab ────────────────────────────────────────────────────────────────

const RosterTab = () => {
  const [roster, setRoster] = useState([]);
  const [summary, setSummary] = useState({
    total_swimmers: 0,
    assigned_swimmers: 0,
    unassigned_swimmers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [newSwimmer, setNewSwimmer] = useState("");
  const [newCoach, setNewCoach] = useState("");
  const [editing, setEditing] = useState(null); // swimmer_name being edited
  const [editCoach, setEditCoach] = useState("");
  const [assigningSwimmer, setAssigningSwimmer] = useState("");
  const [assignCoachName, setAssignCoachName] = useState("");
  const [coaches, setCoaches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState("all");

  const computeSummary = useCallback((rows) => {
    const total = rows.length;
    const assigned = rows.filter((row) => (row.coach_name || "").trim()).length;
    return {
      total_swimmers: total,
      assigned_swimmers: assigned,
      unassigned_swimmers: total - assigned,
    };
  }, []);

  const applyRosterRows = useCallback(
    (rows) => {
      setRoster(rows);
      setSummary(computeSummary(rows));
    },
    [computeSummary],
  );

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/roster`, {
        headers: authHeaders(),
      });
      applyRosterRows(res.data.roster || []);
    } catch (e) {
      setStatus("Error loading roster.");
    } finally {
      setLoading(false);
    }
  }, [applyRosterRows]);

  const fetchCoaches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: authHeaders(),
      });
      const list = (res.data || [])
        .map((u) => (u.coach_name || "").trim())
        .filter(Boolean);
      setCoaches([...new Set(list)].sort());
    } catch {}
  }, []);

  useEffect(() => {
    fetchRoster();
    fetchCoaches();
  }, [fetchRoster, fetchCoaches]);

  const upsert = async (swimmerName, coachName) => {
    try {
      await axios.post(
        `${API}/admin/roster/entry`,
        { swimmer_name: swimmerName, coach_name: coachName },
        { headers: authHeaders() },
      );
      setRoster((prev) => {
        const existingIndex = prev.findIndex(
          (row) => row.swimmer_name === swimmerName,
        );
        const next = [...prev];
        if (existingIndex >= 0) {
          next[existingIndex] = {
            ...next[existingIndex],
            swimmer_name: swimmerName,
            coach_name: coachName,
          };
        } else {
          next.push({ swimmer_name: swimmerName, coach_name: coachName });
        }
        setSummary(computeSummary(next));
        return next;
      });
      setStatus(`Saved: ${swimmerName} → ${coachName}`);
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const remove = async (swimmerName) => {
    if (!window.confirm(`Remove ${swimmerName} from the roster?`)) return;
    try {
      await axios.delete(
        `${API}/admin/roster/${encodeURIComponent(swimmerName)}`,
        { headers: authHeaders() },
      );
      setRoster((prev) => {
        const next = prev.filter((row) => row.swimmer_name !== swimmerName);
        setSummary(computeSummary(next));
        return next;
      });
      setStatus(`Removed ${swimmerName}`);
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const handleAdd = async () => {
    if (!newSwimmer.trim() || !newCoach.trim()) {
      setStatus("Error: Both swimmer name and coach name are required.");
      return;
    }
    await upsert(newSwimmer.trim(), newCoach.trim());
    setNewSwimmer("");
    setNewCoach("");
  };

  const saveEdit = async (swimmerName) => {
    await upsert(swimmerName, editCoach.trim());
    setEditing(null);
  };

  const saveInlineAssign = async (swimmerName, coachName) => {
    const name = (coachName ?? assignCoachName).trim();
    if (!name) return;
    await upsert(swimmerName, name);
    setAssigningSwimmer("");
    setAssignCoachName("");
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const visibleRows = roster
    .filter((row) => {
      const swimmerName = (row.swimmer_name || "").toLowerCase();
      const coachName = (row.coach_name || "").trim();
      const matchesSearch =
        !normalizedSearch || swimmerName.includes(normalizedSearch);
      const matchesFilter =
        assignmentFilter === "assigned"
          ? Boolean(coachName)
          : assignmentFilter === "unassigned"
            ? !coachName
            : true;
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) =>
      compareSwimmerNamesByLastName(a?.swimmer_name, b?.swimmer_name),
    );
  const unassignedRows = visibleRows.filter((row) => !row.coach_name);

  if (loading) return <p>Loading roster…</p>;

  return (
    <div>
      <h2 style={sectionTitle}>Roster Manager</h2>
      <p style={hint}>
        Live roster from your uploaded roster CSV plus parsed meet swimmers.
        Assign or reassign swimmers to coaches.
      </p>

      <div style={statusGrid}>
        <div style={statusCard}>
          <strong>{summary.total_swimmers}</strong>
          <span>Total Swimmers</span>
        </div>
        <div style={statusCard}>
          <strong>{summary.assigned_swimmers}</strong>
          <span>Assigned</span>
        </div>
        <div style={statusCard}>
          <strong>{summary.unassigned_swimmers}</strong>
          <span>Unassigned</span>
        </div>
      </div>

      <div
        style={{
          ...card,
          marginTop: "0.75rem",
          marginBottom: "0.8rem",
          display: "grid",
          gap: "0.55rem",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          <button
            type="button"
            style={
              assignmentFilter === "all"
                ? { ...smallBtn, ...secondaryBtn }
                : smallBtn
            }
            onClick={() => setAssignmentFilter("all")}
          >
            All
          </button>
          <button
            type="button"
            style={
              assignmentFilter === "assigned"
                ? { ...smallBtn, ...secondaryBtn }
                : smallBtn
            }
            onClick={() => setAssignmentFilter("assigned")}
          >
            Assigned
          </button>
          <button
            type="button"
            style={
              assignmentFilter === "unassigned"
                ? { ...smallBtn, ...secondaryBtn }
                : smallBtn
            }
            onClick={() => setAssignmentFilter("unassigned")}
          >
            Unassigned
          </button>
        </div>
        <input
          placeholder="Search swimmers"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={inlineInput}
        />
      </div>

      <div style={{ ...card, marginTop: "0.75rem", marginBottom: "0.8rem" }}>
        <h3 style={cardTitle}>Needs Coach Assignment</h3>
        {unassignedRows.length === 0 ? (
          <p style={hint}>
            {assignmentFilter === "unassigned" || normalizedSearch
              ? "No unassigned swimmers match the current filters."
              : "All swimmers are assigned to a coach."}
          </p>
        ) : (
          <div style={unassignedList}>
            {unassignedRows.map((row) => (
              <div
                key={`unassigned-${row.swimmer_name}`}
                style={unassignedItem}
              >
                <span style={unassignedName}>{row.swimmer_name}</span>

                {assigningSwimmer === row.swimmer_name ? (
                  <div style={unassignedActions}>
                    <select
                      autoFocus
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value)
                          saveInlineAssign(row.swimmer_name, e.target.value);
                      }}
                      style={{
                        ...inlineInput,
                        minWidth: "160px",
                        maxWidth: "220px",
                        cursor: "pointer",
                      }}
                    >
                      <option value="" disabled>
                        — Select coach —
                      </option>
                      {coaches.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      style={{ ...smallBtn, ...dangerBtn }}
                      onClick={() => {
                        setAssigningSwimmer("");
                        setAssignCoachName("");
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    style={smallBtn}
                    onClick={() => {
                      setAssigningSwimmer(row.swimmer_name);
                      setAssignCoachName("");
                    }}
                  >
                    Assign Coach
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="table-scroll admin-table-scroll">
        <table style={table} className="stack-table admin-stack-table">
          <thead>
            <tr>
              <th style={th}>Swimmer</th>
              <th style={th}>Coach</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => (
              <tr
                key={row.swimmer_name}
                style={i % 2 === 0 ? rowEven : rowOdd}
                className="stack-table-row"
              >
                <td style={td} data-label="Swimmer">
                  {row.swimmer_name}
                </td>
                <td style={td} data-label="Coach">
                  {editing === row.swimmer_name ? (
                    <input
                      value={editCoach}
                      onChange={(e) => setEditCoach(e.target.value)}
                      style={inlineInput}
                      onKeyDown={(e) =>
                        e.key === "Enter" && saveEdit(row.swimmer_name)
                      }
                    />
                  ) : row.coach_name ? (
                    row.coach_name
                  ) : (
                    <span style={mutedText}>Unassigned</span>
                  )}
                </td>
                <td
                  style={td}
                  data-label="Actions"
                  className="stack-table-actions"
                >
                  {editing === row.swimmer_name ? (
                    <>
                      <button
                        style={smallBtn}
                        onClick={() => saveEdit(row.swimmer_name)}
                      >
                        Save
                      </button>
                      <button
                        style={{ ...smallBtn, ...dangerBtn }}
                        onClick={() => setEditing(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        style={smallBtn}
                        onClick={() => {
                          setEditing(row.swimmer_name);
                          setEditCoach(row.coach_name || "");
                        }}
                      >
                        Edit
                      </button>
                      <button
                        style={{ ...smallBtn, ...dangerBtn }}
                        onClick={() => remove(row.swimmer_name)}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}

            {visibleRows.length === 0 ? (
              <tr style={rowEven} className="stack-table-row">
                <td style={td} data-label="Swimmer" colSpan={3}>
                  <span style={mutedText}>
                    No swimmers match the current filters.
                  </span>
                </td>
              </tr>
            ) : null}

            {/* Add new row */}
            <tr style={rowAdd} className="stack-table-row">
              <td style={td} data-label="Swimmer">
                <input
                  placeholder="Swimmer Full Name"
                  value={newSwimmer}
                  onChange={(e) => setNewSwimmer(e.target.value)}
                  style={inlineInput}
                />
              </td>
              <td style={td} data-label="Coach">
                <input
                  placeholder="Coach Name"
                  value={newCoach}
                  onChange={(e) => setNewCoach(e.target.value)}
                  style={inlineInput}
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
              </td>
              <td
                style={td}
                data-label="Actions"
                className="stack-table-actions"
              >
                <button style={smallBtn} onClick={handleAdd}>
                  + Add
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <StatusMessage msg={status} />
    </div>
  );
};

// ── Users Tab ─────────────────────────────────────────────────────────────────

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [editCoach, setEditCoach] = useState("");
  const [editAdmin, setEditAdmin] = useState(false);
  const [editLogoDataUrl, setEditLogoDataUrl] = useState("");
  const [renameOld, setRenameOld] = useState("");
  const [renameNew, setRenameNew] = useState("");
  const [renameStatus, setRenameStatus] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: authHeaders(),
      });
      setUsers(res.data || []);
    } catch (e) {
      setStatus("Error loading users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setEditLogoDataUrl(ev.target.result || "");
    reader.readAsDataURL(file);
  };

  const saveEdit = async (username) => {
    try {
      const payload = { coach_name: editCoach.trim(), is_admin: editAdmin };
      if (editLogoDataUrl) payload.profile_image_data_url = editLogoDataUrl;
      await axios.patch(`${API}/admin/users/${username}`, payload, {
        headers: authHeaders(),
      });
      setStatus(`Updated ${username}`);
      setEditing(null);
      fetchUsers();
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const deleteUser = async (username) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`))
      return;
    try {
      await axios.delete(`${API}/admin/users/${username}`, {
        headers: authHeaders(),
      });
      setStatus(`Deleted ${username}`);
      fetchUsers();
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const renameCoachInRoster = async () => {
    const old = renameOld.trim();
    const next = renameNew.trim();
    if (!old || !next) {
      setRenameStatus("Both fields are required.");
      return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/roster/rename-coach`,
        { old_name: old, new_name: next },
        { headers: authHeaders() },
      );
      setRenameStatus(
        `Done — renamed ${res.data.renamed} roster slot(s) from "${old}" to "${next}".`,
      );
      setRenameOld("");
      setRenameNew("");
    } catch (e) {
      setRenameStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  if (loading) return <p>Loading users…</p>;

  return (
    <div>
      <h2 style={sectionTitle}>User Management</h2>
      <p style={hint}>
        Edit coach assignments or admin access for any account.
      </p>

      <div className="table-scroll admin-table-scroll">
        <table style={table} className="stack-table admin-stack-table">
          <thead>
            <tr>
              <th style={th}>Username</th>
              <th style={th}>Coach Name</th>
              <th style={th}>Admin</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <React.Fragment key={u.username}>
              <tr
                style={i % 2 === 0 ? rowEven : rowOdd}
                className="stack-table-row"
              >
                <td style={td} data-label="Username">
                  {u.username}
                </td>
                <td style={td} data-label="Coach Name">
                  {editing === u.username ? (
                    <input
                      value={editCoach}
                      onChange={(e) => setEditCoach(e.target.value)}
                      style={inlineInput}
                    />
                  ) : (
                    u.coach_name
                  )}
                </td>
                <td style={{ ...td, textAlign: "center" }} data-label="Admin">
                  {editing === u.username ? (
                    <input
                      type="checkbox"
                      checked={editAdmin}
                      onChange={(e) => setEditAdmin(e.target.checked)}
                    />
                  ) : u.is_admin ? (
                    "✅"
                  ) : (
                    "—"
                  )}
                </td>
                <td
                  style={td}
                  data-label="Actions"
                  className="stack-table-actions"
                >
                  {editing === u.username ? (
                    <>
                      <button
                        style={smallBtn}
                        onClick={() => saveEdit(u.username)}
                      >
                        Save
                      </button>
                      <button
                        style={{ ...smallBtn, ...dangerBtn }}
                        onClick={() => setEditing(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        style={smallBtn}
                        onClick={() => {
                          setEditing(u.username);
                          setEditCoach(u.coach_name);
                          setEditAdmin(u.is_admin);
                          setEditLogoDataUrl("");
                        }}
                      >
                        Edit
                      </button>
                      <button
                        style={{ ...smallBtn, ...dangerBtn }}
                        onClick={() => deleteUser(u.username)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
              {editing === u.username && (
                <tr style={{ background: "rgba(0,245,212,0.05)" }}>
                  <td colSpan={4} style={{ ...td, padding: "0.75rem 1rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                      <div>
                        <p style={{ ...hint, marginBottom: "0.35rem" }}>Team Logo</p>
                        {(editLogoDataUrl || u.profile_image_data_url) && (
                          <img
                            src={editLogoDataUrl || u.profile_image_data_url}
                            alt="logo preview"
                            style={{ width: 64, height: 64, objectFit: "contain", borderRadius: 8, background: "rgba(255,255,255,0.06)", marginBottom: "0.4rem", display: "block" }}
                          />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoFile}
                          style={{ fontSize: "0.78rem", color: "#a6bce6" }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <StatusMessage msg={status} />

      <div style={{ ...card, marginTop: "1.2rem" }}>
        <h3 style={{ ...sectionTitle, fontSize: "0.9rem", marginBottom: "0.5rem" }}>
          Fix Roster Coach Name
        </h3>
        <p style={hint}>
          Use this if a coach was renamed but their swimmers still show under the old name in the Teams view.
        </p>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          <input
            placeholder="Old name (e.g. Coach Megan)"
            value={renameOld}
            onChange={(e) => setRenameOld(e.target.value)}
            style={{ ...inlineInput, minWidth: "160px" }}
          />
          <span style={{ color: "#6884b7", fontWeight: 700 }}>→</span>
          <input
            placeholder="New name (e.g. Megan)"
            value={renameNew}
            onChange={(e) => setRenameNew(e.target.value)}
            style={{ ...inlineInput, minWidth: "160px" }}
          />
          <button style={smallBtn} onClick={renameCoachInRoster}>
            Rename in Roster
          </button>
        </div>
        {renameStatus && (
          <p style={{ marginTop: "0.5rem", color: renameStatus.startsWith("Error") ? "#ff93b0" : "#00f5d4", fontSize: "0.82rem" }}>
            {renameStatus}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Results Override Tab ──────────────────────────────────────────────────────

const ResultsTab = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null); // "name||event"
  const [editPoints, setEditPoints] = useState("");
  const [editFinalTime, setEditFinalTime] = useState("");
  const [editEvalPoints, setEditEvalPoints] = useState("");

  const parseOptionalNumber = (value) => {
    if (value === "") return null;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/results`, {
        headers: authHeaders(),
      });
      setResults(res.data.results || []);
    } catch (e) {
      setStatus("Error loading results.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const saveOverride = async (r) => {
    try {
      const payload = {
        swimmer_name: r.name,
        event: r.event,
      };

      const finalTime = parseOptionalNumber(editFinalTime);
      const fantasyPoints = parseOptionalNumber(editPoints);
      const evaluationPoints = parseOptionalNumber(editEvalPoints);

      if (finalTime !== null) payload.final_time = finalTime;
      if (fantasyPoints !== null) payload.fantasy_points = fantasyPoints;
      if (evaluationPoints !== null)
        payload.evaluation_points = evaluationPoints;

      if (
        payload.final_time == null &&
        payload.fantasy_points == null &&
        payload.evaluation_points == null
      ) {
        setStatus("Error: Enter at least one value to override.");
        return;
      }

      await axios.patch(`${API}/admin/results`, payload, {
        headers: authHeaders(),
      });
      setStatus(`Override saved for ${r.name} — ${r.event}`);
      setEditing(null);
      fetchResults();
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const clearOverride = async (r) => {
    if (!window.confirm(`Remove override for ${r.name} — ${r.event}?`)) return;
    try {
      await axios.delete(
        `${API}/admin/results/${encodeURIComponent(r.name)}?event=${encodeURIComponent(r.event)}`,
        { headers: authHeaders() },
      );
      setStatus(`Override cleared for ${r.name}`);
      fetchResults();
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const rowKey = (r) => `${r.name}||${r.event}`;

  if (loading) return <p>Loading results…</p>;

  if (results.length === 0)
    return (
      <div>
        <h2 style={sectionTitle}>Results Override</h2>
        <p style={hint}>
          No results loaded yet. Upload a roster CSV and SD3 file first using
          the Upload Files tab.
        </p>
      </div>
    );

  return (
    <div>
      <h2 style={sectionTitle}>Results Override</h2>
      <p style={hint}>
        Edit a swimmer&apos;s final time to auto-calculate drop points, review
        SD3 place/team points, adjust total event points if needed, and set
        one-time end-of-season evaluation points.
      </p>

      <div
        style={{ overflowX: "auto" }}
        className="table-scroll admin-table-scroll"
      >
        <table style={table} className="stack-table admin-stack-table">
          <thead>
            <tr>
              <th style={th}>Swimmer</th>
              <th style={th}>Coach</th>
              <th style={th}>Event</th>
              <th style={th}>Baseline</th>
              <th style={th}>Final</th>
              <th style={th}>Drop</th>
              <th style={th}>Place</th>
              <th style={th}>Team Pts</th>
              <th style={th}>Drop Pts</th>
              <th style={th}>Total Pts</th>
              <th style={th}>Eval Pts</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => {
              const key = rowKey(r);
              const isEditing = editing === key;
              return (
                <tr
                  key={key}
                  style={i % 2 === 0 ? rowEven : rowOdd}
                  className="stack-table-row"
                >
                  <td style={td} data-label="Swimmer">
                    {r.name}
                  </td>
                  <td style={td} data-label="Coach">
                    {r.coach ?? "—"}
                  </td>
                  <td style={td} data-label="Event">
                    {r.event}
                  </td>
                  <td style={td} data-label="Baseline">
                    {r.baseline_time ?? "—"}
                  </td>
                  <td style={td} data-label="Final">
                    {isEditing ? (
                      <input
                        value={editFinalTime}
                        onChange={(e) => setEditFinalTime(e.target.value)}
                        style={{ ...inlineInput, width: "70px" }}
                        type="number"
                        step="0.01"
                      />
                    ) : (
                      (r.final_time ?? "—")
                    )}
                  </td>
                  <td style={td} data-label="Drop">
                    {r.time_drop ?? "—"}
                  </td>
                  <td style={td} data-label="Place">
                    {r.place ?? "—"}
                  </td>
                  <td style={td} data-label="Team Pts">
                    {r.team_points ?? "0"}
                  </td>
                  <td style={td} data-label="Drop Pts">
                    {r.time_drop_points ?? "0"}
                  </td>
                  <td style={td} data-label="Total Pts">
                    {isEditing ? (
                      <input
                        value={editPoints}
                        onChange={(e) => setEditPoints(e.target.value)}
                        style={{ ...inlineInput, width: "70px" }}
                        type="number"
                        step="0.01"
                      />
                    ) : (
                      <>
                        {r.fantasy_points ?? "0"}
                        {r.overridden && " ✏️"}
                      </>
                    )}
                  </td>
                  <td style={td} data-label="Eval Pts">
                    {isEditing ? (
                      <input
                        value={editEvalPoints}
                        onChange={(e) => setEditEvalPoints(e.target.value)}
                        style={{ ...inlineInput, width: "70px" }}
                        type="number"
                        step="0.01"
                      />
                    ) : (
                      (r.evaluation_points ?? "0")
                    )}
                  </td>
                  <td
                    style={td}
                    data-label="Actions"
                    className="stack-table-actions"
                  >
                    {isEditing ? (
                      <>
                        <button
                          style={smallBtn}
                          onClick={() => saveOverride(r)}
                        >
                          Save
                        </button>
                        <button
                          style={{ ...smallBtn, ...dangerBtn }}
                          onClick={() => setEditing(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          style={smallBtn}
                          onClick={() => {
                            setEditing(key);
                            setEditPoints(r.fantasy_points ?? "");
                            setEditFinalTime(r.final_time ?? "");
                            setEditEvalPoints(r.evaluation_points ?? "");
                          }}
                        >
                          Override
                        </button>
                        {r.overridden && (
                          <button
                            style={{ ...smallBtn, ...dangerBtn }}
                            onClick={() => clearOverride(r)}
                          >
                            Clear
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <StatusMessage msg={status} />
    </div>
  );
};

// ── Swimmer Results Tab ───────────────────────────────────────────────────────

const SwimmerResultsTab = () => {
  const [swimmers, setSwimmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [openSwimmer, setOpenSwimmer] = useState(null);
  const [openEvent, setOpenEvent] = useState(null); // "swimmerName||eventName"
  const [editing, setEditing] = useState(null); // "swimmerName||eventName||meetId"
  const [editTime, setEditTime] = useState("");
  const [status, setStatus] = useState("");

  const fetchSwimmers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/swimmer-results`, {
        headers: authHeaders(),
      });
      setSwimmers(res.data.swimmers || []);
    } catch (e) {
      setStatus("Error loading swimmer results.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSwimmers();
  }, [fetchSwimmers]);

  const saveEdit = async (swimmer, eventName, meet) => {
    const parsed = parseFloat(editTime);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setStatus("Error: Enter a valid time in seconds.");
      return;
    }
    try {
      const res = await axios.patch(
        `${API}/admin/results/meet-time`,
        {
          swimmer_name: swimmer.name,
          event: eventName,
          meet_id: meet.meet_id,
          final_time: parsed,
        },
        { headers: authHeaders() },
      );
      setStatus(
        `Saved — ${swimmer.name} ${eventName} at ${meet.meet_label}: ${parsed}s. ` +
          `Total fantasy: ${res.data.swimmer_total_fantasy_points ?? "—"}`,
      );
      setEditing(null);
      fetchSwimmers();
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const filteredSwimmers = filter.trim()
    ? swimmers.filter((s) => s.name.toLowerCase().includes(filter.toLowerCase()))
    : swimmers;

  if (loading) return <p>Loading swimmer results…</p>;

  return (
    <div>
      <h2 style={sectionTitle}>Swimmer Results</h2>
      <p style={hint}>
        Find a swimmer, expand an event, then edit any individual meet time.
        Times are in seconds. Changes cascade to points and coach scores immediately.
      </p>

      {swimmers.length === 0 ? (
        <p style={hint}>No results loaded yet. Upload a roster CSV and SD3 file first.</p>
      ) : (
        <>
          <input
            placeholder="Search by swimmer name…"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setOpenSwimmer(null);
              setOpenEvent(null);
            }}
            style={{ ...inlineInput, width: "280px", marginBottom: "0.75rem" }}
          />
          <div>
            {filteredSwimmers.map((swimmer) => {
              const isOpen = openSwimmer === swimmer.name;
              return (
                <div key={swimmer.name} style={swimmerBlock}>
                  {/* Swimmer header row */}
                  <button
                    style={swimmerBtn}
                    onClick={() => {
                      setOpenSwimmer(isOpen ? null : swimmer.name);
                      setOpenEvent(null);
                      setEditing(null);
                      setStatus("");
                    }}
                  >
                    <span>{swimmer.name}</span>
                    <span style={{ color: "#8ea7d8", fontSize: "0.78rem" }}>
                      {swimmer.coach || "Unassigned"} &nbsp;{isOpen ? "▲" : "▼"}
                    </span>
                  </button>

                  {/* Events list */}
                  {isOpen && (
                    <div style={{ paddingLeft: "0.75rem" }}>
                      {swimmer.events.map(({ event: eventName, meets }) => {
                        const evKey = `${swimmer.name}||${eventName}`;
                        const evOpen = openEvent === evKey;
                        return (
                          <div key={eventName} style={eventBlock}>
                            {/* Event header */}
                            <button
                              style={eventBtn}
                              onClick={() => {
                                setOpenEvent(evOpen ? null : evKey);
                                setEditing(null);
                                setStatus("");
                              }}
                            >
                              <span>{eventName}</span>
                              <span style={{ color: "#8ea7d8", fontSize: "0.76rem" }}>
                                {meets.length} meet{meets.length !== 1 ? "s" : ""} &nbsp;{evOpen ? "▲" : "▼"}
                              </span>
                            </button>

                            {/* Meet times table */}
                            {evOpen && (
                              <div style={{ overflowX: "auto" }}>
                                <table style={{ ...table, marginTop: "0.4rem" }} className="stack-table admin-stack-table">
                                  <thead>
                                    <tr>
                                      <th style={th}>Meet</th>
                                      <th style={th}>Baseline (s)</th>
                                      <th style={th}>Time (s)</th>
                                      <th style={th}>Drop (s)</th>
                                      <th style={th}>Drop Pts</th>
                                      <th style={th}>Team Pts</th>
                                      <th style={th}>Fantasy Pts</th>
                                      <th style={th}>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {meets.map((meet, i) => {
                                      const meetKey = `${swimmer.name}||${eventName}||${meet.meet_id}`;
                                      const isEditing = editing === meetKey;
                                      return (
                                        <tr key={meet.meet_id} style={i % 2 === 0 ? rowEven : rowOdd} className="stack-table-row">
                                          <td style={td} data-label="Meet">
                                            {meet.meet_label || meet.meet_id}
                                            {meet.overridden && (
                                              <span style={{ color: "#ffcd87", marginLeft: "0.4rem", fontSize: "0.7rem" }}>
                                                edited
                                              </span>
                                            )}
                                          </td>
                                          <td style={td} data-label="Baseline (s)">{meet.baseline_time ?? "—"}</td>
                                          <td style={td} data-label="Time (s)">
                                            {isEditing ? (
                                              <input
                                                value={editTime}
                                                onChange={(e) => setEditTime(e.target.value)}
                                                style={{ ...inlineInput, width: "80px" }}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                autoFocus
                                              />
                                            ) : (
                                              meet.original_time ?? "—"
                                            )}
                                          </td>
                                          <td style={td} data-label="Drop (s)">{meet.time_drop ?? "—"}</td>
                                          <td style={td} data-label="Drop Pts">{meet.time_drop_points ?? "—"}</td>
                                          <td style={td} data-label="Team Pts">{meet.team_points ?? "—"}</td>
                                          <td style={td} data-label="Fantasy Pts">{meet.fantasy_points ?? "—"}</td>
                                          <td style={td} data-label="Actions">
                                            {isEditing ? (
                                              <>
                                                <button style={smallBtn} onClick={() => saveEdit(swimmer, eventName, meet)}>
                                                  Save
                                                </button>
                                                <button style={{ ...smallBtn, ...dangerBtn }} onClick={() => setEditing(null)}>
                                                  Cancel
                                                </button>
                                              </>
                                            ) : (
                                              <button
                                                style={smallBtn}
                                                onClick={() => {
                                                  setEditing(meetKey);
                                                  setEditTime(meet.original_time != null ? String(meet.original_time) : "");
                                                  setStatus("");
                                                }}
                                              >
                                                Edit
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <StatusMessage msg={status} />
    </div>
  );
};

// ── Draft Setup Tab ───────────────────────────────────────────────────────────

const DraftTab = () => {
  const [users, setUsers] = useState([]);
  const [selectedCoaches, setSelectedCoaches] = useState([]);
  const [randomizeOrder, setRandomizeOrder] = useState(false);
  const [draftStatus, setDraftStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  const uniqueCoaches = useMemo(() => {
    return Array.from(
      new Set(
        users.map((user) => (user.coach_name || "").trim()).filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const fetchDraftData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, draftRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: authHeaders() }),
        axios.get(`${API}/admin/draft/status`, { headers: authHeaders() }),
      ]);

      setUsers(usersRes.data || []);
      setDraftStatus(draftRes.data?.draft || null);
      setStatus("");
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDraftData();
  }, [fetchDraftData]);

  const toggleCoach = (coachName) => {
    setSelectedCoaches((prev) =>
      prev.includes(coachName)
        ? prev.filter((coach) => coach !== coachName)
        : [...prev, coachName],
    );
  };

  const startDraft = async () => {
    if (selectedCoaches.length < 2) {
      setStatus("Error: Select at least 2 coaches.");
      return;
    }

    if (
      !window.confirm(
        "Start new season draft? This will reset current roster assignments.",
      )
    ) {
      return;
    }

    try {
      const res = await axios.post(
        `${API}/admin/draft/start`,
        { coaches: selectedCoaches, randomize_order: randomizeOrder },
        { headers: authHeaders() },
      );
      setStatus(res.data.message || "Season draft started.");
      fetchDraftData();
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const startMockDraft = async () => {
    if (selectedCoaches.length < 2) {
      setStatus("Error: Select at least 2 coaches.");
      return;
    }

    if (
      !window.confirm(
        "Start mock draft with selected coaches? This will not reset season roster assignments.",
      )
    ) {
      return;
    }

    try {
      const res = await axios.post(
        `${API}/admin/draft/mock/start`,
        { coaches: selectedCoaches, randomize_order: randomizeOrder },
        { headers: authHeaders() },
      );
      setStatus(res.data.message || "Mock draft started.");
      fetchDraftData();
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const clearMockDraft = async () => {
    if (!window.confirm("Clear mock draft data now?")) {
      return;
    }

    try {
      const res = await axios.post(
        `${API}/admin/draft/mock/clear`,
        {},
        { headers: authHeaders() },
      );
      setStatus(res.data.message || "Mock draft cleared.");
      fetchDraftData();
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const stopDraft = async () => {
    if (!window.confirm("Stop the current draft now?")) {
      return;
    }

    try {
      const res = await axios.post(
        `${API}/admin/draft/stop`,
        {},
        { headers: authHeaders() },
      );
      setStatus(res.data?.message || "Draft stopped by admin.");
      fetchDraftData();
    } catch (e) {
      setStatus("Error: " + (e.response?.data?.detail ?? e.message));
    }
  };

  if (loading) return <p>Loading draft setup…</p>;

  return (
    <div>
      <h2 style={sectionTitle}>Season Draft Setup</h2>
      <p style={hint}>
        Select coaches in your preferred order, then either start a season draft
        or a mock draft. By default, selected order is used for round 1.
      </p>

      <div style={card}>
        <h3 style={cardTitle}>Select Coaches</h3>
        <div style={checkboxGrid}>
          {uniqueCoaches.map((coachName) => (
            <label key={coachName} style={checkLabel}>
              <input
                type="checkbox"
                checked={selectedCoaches.includes(coachName)}
                onChange={() => toggleCoach(coachName)}
              />
              <span>{coachName}</span>
            </label>
          ))}
        </div>
        <label style={{ ...checkLabel, marginTop: "0.75rem" }}>
          <input
            type="checkbox"
            checked={randomizeOrder}
            onChange={(event) => setRandomizeOrder(event.target.checked)}
          />
          <span>Randomize draft order at start</span>
        </label>
        {selectedCoaches.length ? (
          <p style={{ ...hint, marginTop: "0.55rem", marginBottom: 0 }}>
            Planned round 1 order:{" "}
            {randomizeOrder
              ? "Randomized at start"
              : selectedCoaches.join(" → ")}
          </p>
        ) : null}
        <div
          style={{
            display: "flex",
            gap: "0.6rem",
            flexWrap: "wrap",
            marginTop: "0.9rem",
          }}
        >
          <button style={btn} onClick={startDraft}>
            Start Season Draft
          </button>
          <button style={btn} onClick={startMockDraft}>
            Start Mock Draft
          </button>
          <button
            style={
              draftStatus?.active
                ? { ...btn, ...resetBtn, opacity: 0.6 }
                : { ...btn, ...resetBtn }
            }
            onClick={clearMockDraft}
            disabled={draftStatus?.active}
            title={
              draftStatus?.active
                ? "Stop draft first before clearing"
                : "Clear draft data"
            }
          >
            Clear Draft Data
          </button>
        </div>
        {draftStatus?.active ? (
          <button
            style={{
              ...btn,
              ...resetBtn,
              marginTop: "0.9rem",
              marginLeft: "0.6rem",
            }}
            onClick={stopDraft}
          >
            Stop Draft
          </button>
        ) : null}
      </div>

      <div style={{ ...card, marginTop: "1rem" }}>
        <h3 style={cardTitle}>Current Draft Status</h3>
        {!draftStatus ? (
          <p>No draft status available.</p>
        ) : (
          <div style={statusGrid}>
            <div style={statusCard}>
              <strong>
                {draftStatus.active
                  ? "Active"
                  : draftStatus.completed
                    ? "Completed"
                    : "Inactive"}
              </strong>
              <span>Draft State</span>
            </div>
            <div style={statusCard}>
              <strong>{draftStatus.is_mock ? "Mock" : "Season"}</strong>
              <span>Draft Type</span>
            </div>
            <div style={statusCard}>
              <strong>{draftStatus.coaches?.length || 0}</strong>
              <span>Coaches</span>
            </div>
            <div style={statusCard}>
              <strong>{draftStatus.picks?.length || 0}</strong>
              <span>Total Picks</span>
            </div>
            <div style={statusCard}>
              <strong>{draftStatus.turn?.current_coach || "—"}</strong>
              <span>Current Turn</span>
            </div>
            <div style={statusCard}>
              <strong>
                {Array.isArray(draftStatus.coaches) &&
                draftStatus.coaches.length
                  ? draftStatus.coaches.join(" → ")
                  : "—"}
              </strong>
              <span>Round 1 Order</span>
            </div>
          </div>
        )}
      </div>

      <StatusMessage msg={status} />
    </div>
  );
};

// ── Game Tab ──────────────────────────────────────────────────────────────────

const GameTab = () => {
  const [statusSummary, setStatusSummary] = useState(null);
  const [matchupState, setMatchupState] = useState(null);
  const [allCoaches, setAllCoaches] = useState([]);
  const [selectedCoaches, setSelectedCoaches] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [actionStatus, setActionStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [matchupDetails, setMatchupDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  const openMatchupDetails = async (week, matchupIndex) => {
    setMatchupDetails({ loading: true, week, matchupIndex });
    setDetailsLoading(true);
    setDetailsError("");
    try {
      const res = await axios.get(
        `${API}/matchups/${week}/${matchupIndex}/details`,
      );
      setMatchupDetails({ ...res.data, loading: false });
    } catch (e) {
      setDetailsError(e.response?.data?.detail ?? e.message);
      setMatchupDetails({ loading: false, error: true, week, matchupIndex });
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeMatchupDetails = () => {
    setMatchupDetails(null);
    setDetailsError("");
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, matchupRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/status`, { headers: authHeaders() }),
        axios.get(`${API}/matchups`),
        axios.get(`${API}/admin/users`, { headers: authHeaders() }),
      ]);
      setStatusSummary(statusRes.data);
      setMatchupState(matchupRes.data);
      const coaches = (Array.isArray(usersRes.data) ? usersRes.data : [])
        .map((u) => (u.coach_name || "").trim())
        .filter(Boolean)
        .sort();
      setAllCoaches(coaches);
      // Pre-select: existing participating_coaches from season, or all coaches
      const existing = statusRes.data?.season?.participating_coaches;
      setSelectedCoaches(new Set(existing?.length ? existing : coaches));
    } catch {
      setActionStatus("Error loading game state.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleGameLock = async (locked) => {
    setBusy(true);
    setActionStatus("");
    try {
      const res = await axios.post(`${API}/admin/game-lock`, { locked }, { headers: authHeaders() });
      setActionStatus(res.data.message || (locked ? "Game locked." : "Game unlocked."));
      fetchAll();
    } catch (e) {
      setActionStatus("Error: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setBusy(false);
    }
  };

  const toggleSeason = async (start) => {
    setBusy(true);
    setActionStatus("");
    try {
      const body = start
        ? { participating_coaches: Array.from(selectedCoaches) }
        : {};
      const res = await axios.post(`${API}/admin/season/${start ? "start" : "end"}`, body, { headers: authHeaders() });
      setActionStatus(res.data.message || (start ? "Season started." : "Season ended."));
      fetchAll();
    } catch (e) {
      setActionStatus("Error: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setBusy(false);
    }
  };

  const toggleCoach = (coach) => {
    setSelectedCoaches((prev) => {
      const next = new Set(prev);
      if (next.has(coach)) next.delete(coach);
      else next.add(coach);
      return next;
    });
  };

  const resetMatchups = async () => {
    if (!window.confirm("Reset matchups to Week 1? All Swiss bracket history will be lost.")) return;
    setBusy(true);
    setActionStatus("");
    try {
      const res = await axios.post(`${API}/admin/matchups/generate`, {}, { headers: authHeaders() });
      setMatchupState(res.data.state);
      setActionStatus("Reset to Week 1 with new random pairings.");
    } catch (e) {
      setActionStatus("Error: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setBusy(false);
    }
  };

  const recalculateMatchupScores = async () => {
    if (!window.confirm("Recalculate all bracket point totals from current results? Wins, losses, and pairings are preserved.")) return;
    setBusy(true);
    setActionStatus("");
    try {
      await axios.post(`${API}/admin/matchups/recalculate-scores`, {}, { headers: authHeaders() });
      setActionStatus("Scores recalculated. Refreshing...");
      const matchupRes = await axios.get(`${API}/matchups`, { headers: authHeaders() });
      setMatchupState(matchupRes.data);
    } catch (e) {
      setActionStatus("Error: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setBusy(false);
    }
  };

  const gameLocked = statusSummary?.game_locked ?? false;
  const season = statusSummary?.season ?? {};
  const matchups = matchupState?.matchups || [];
  const generated = matchupState?.generated ?? false;
  const currentWeek = matchupState?.current_week ?? 1;
  const weekHistory = matchupState?.week_history || [];
  const swissRecords = matchupState?.swiss_records || {};

  const coachStandings = Object.entries(swissRecords)
    .map(([coach, rec]) => ({ coach, ...rec }))
    .sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (a.losses !== b.losses) return a.losses - b.losses;
      return (b.total_season_points || 0) - (a.total_season_points || 0);
    });

  const controlLabel = { fontSize: "0.72rem", color: "#9fb7e3", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 };

  if (loading) return <p style={{ color: "#9fb7e3" }}>Loading game state…</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem" }}>
      <h2 style={sectionTitle}>Game Controls</h2>

      {/* ── Controls row ── */}
      <div style={card}>
        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <span style={controlLabel}>Game Status</span>
            <span style={{ fontSize: "1rem", fontWeight: 800, color: gameLocked ? "#ffe6a6" : "#bbffd6" }}>
              {gameLocked ? "Locked (trades blocked)" : "Unlocked"}
            </span>
            <button
              onClick={() => toggleGameLock(!gameLocked)}
              disabled={busy}
              style={gameLocked ? { ...btn, ...unlockBtn } : { ...btn, ...lockBtn }}
            >
              {gameLocked ? "Unlock Game" : "Lock Game"}
            </button>
          </div>

          <div style={{ width: "1px", background: "rgba(86,122,198,0.3)", alignSelf: "stretch" }} />

          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <span style={controlLabel}>Season</span>
            <span style={{ fontSize: "1rem", fontWeight: 800, color: season.active ? "#00f5d4" : "#ff93b0" }}>
              {season.active
                ? `Active (${season.season_year}) — ${season.participating_coaches?.length ?? "?"} coaches`
                : season.started_at
                ? `Ended (${season.season_year})`
                : "Not Started"}
            </span>
            {season.active ? (
              <button
                onClick={() => { if (window.confirm("End the current season?")) toggleSeason(false); }}
                disabled={busy}
                style={{ ...btn, background: "rgba(255,92,122,0.18)", color: "#ff93b0", borderColor: "rgba(255,92,122,0.65)" }}
              >
                End Season
              </button>
            ) : (
              <>
                {allCoaches.length > 0 && (
                  <div style={{ marginTop: "0.25rem" }}>
                    <span style={{ ...controlLabel, display: "block", marginBottom: "0.3rem" }}>
                      Participating Coaches ({selectedCoaches.size}/{allCoaches.length})
                    </span>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                      {allCoaches.map((coach) => (
                        <label key={coach} style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontSize: "0.82rem", color: selectedCoaches.has(coach) ? "#eaf3ff" : "#6884b7", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={selectedCoaches.has(coach)}
                            onChange={() => toggleCoach(coach)}
                            style={{ accentColor: "#00f5d4" }}
                          />
                          {coach}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  onClick={() => {
                    if (selectedCoaches.size < 2) { setActionStatus("Select at least 2 coaches."); return; }
                    if (window.confirm(`Start season with ${selectedCoaches.size} coaches? Week 1 matchups will be randomly generated.`)) toggleSeason(true);
                  }}
                  disabled={busy}
                  style={{ ...btn, background: "rgba(0,196,154,0.18)", color: "#a8ffe0", borderColor: "rgba(0,196,154,0.65)", marginTop: "0.35rem" }}
                >
                  Start Season
                </button>
              </>
            )}
          </div>
        </div>
        <StatusMessage msg={actionStatus} />
      </div>

      {/* ── Swiss Standings ── */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", marginBottom: "0.6rem" }}>
          <h3 style={cardTitle}>Swiss Standings {generated ? `— Week ${currentWeek}` : ""}</h3>
          <button onClick={fetchAll} style={secondaryBtn}>Refresh</button>
        </div>
        {coachStandings.length === 0 ? (
          <p style={hint}>Start a season to initialize standings.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
            {coachStandings.map((c, rank) => (
              <div key={c.coach} style={coachScoreRow}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                  <span style={{ color: "#6884b7", fontSize: "0.72rem", width: "1.1rem", textAlign: "right" }}>{rank + 1}.</span>
                  <span style={coachScoreName}>{c.coach}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <span style={{ color: "#ffe6a6", fontWeight: 700, fontSize: "0.88rem" }}>
                    {c.wins}-{c.losses}
                  </span>
                  <span style={coachScorePts}>{Number(c.total_season_points || 0).toFixed(2)} pts</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Current Week Matchups ── */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.6rem" }}>
          <h3 style={cardTitle}>Week {currentWeek} Matchups</h3>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button onClick={recalculateMatchupScores} disabled={busy} style={{ ...btn, background: "#1e6b3a", fontSize: "0.72rem" }}>
              Recalculate Scores
            </button>
            <button onClick={resetMatchups} disabled={busy} style={{ ...btn, ...resetBtn, fontSize: "0.72rem" }}>
              Reset to Week 1
            </button>
          </div>
        </div>

        {!generated ? (
          <p style={hint}>No matchups yet. Start a season to auto-generate Week 1.</p>
        ) : (
          <>
            <p style={{ ...hint, marginBottom: "0.45rem" }}>
              Matchups advance automatically when a scoring meet is uploaded. The first in-season meet sets the baseline.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {matchups.map((pair, i) => {
                const isBye = pair[1]?.isBye;
                const rec0 = swissRecords[pair[0]?.coach] || {};
                const rec1 = !isBye ? (swissRecords[pair[1]?.coach] || {}) : {};
                return (
                  <div key={i} style={matchupAdminRow}>
                    <div style={{ flex: 1 }}>
                      <div style={matchupAdminLabel}>
                        {pair[0]?.coach}
                        <span style={{ color: "#ffe6a6", marginLeft: "0.35rem", fontSize: "0.72rem", fontWeight: 700 }}>
                          ({rec0.wins ?? 0}-{rec0.losses ?? 0})
                        </span>
                        <span style={{ color: "#6884b7", margin: "0 0.3rem", fontSize: "0.72rem" }}>vs</span>
                        {isBye ? (
                          <span style={{ color: "#6884b7", fontStyle: "italic", fontSize: "0.82rem" }}>Bye</span>
                        ) : (
                          <>
                            {pair[1]?.coach}
                            <span style={{ color: "#ffe6a6", marginLeft: "0.35rem", fontSize: "0.72rem", fontWeight: 700 }}>
                              ({rec1.wins ?? 0}-{rec1.losses ?? 0})
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", fontSize: "0.72rem", color: "#8ea8d8" }}>
                      <span>{Number(pair[0]?.total_points || 0).toFixed(2)} pts</span>
                      {!isBye && <span>{Number(pair[1]?.total_points || 0).toFixed(2)} pts</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ── Week History ── */}
      {weekHistory.length > 0 && (
        <div style={card}>
          <h3 style={{ ...cardTitle, marginBottom: "0.55rem" }}>Completed Weeks</h3>
          {weekHistory.map((week) => (
            <div key={week.week} style={{ marginBottom: "0.75rem" }}>
              <strong style={{ color: "#cddcf8", fontSize: "0.84rem" }}>Week {week.week}</strong>
              <div style={{ marginTop: "0.3rem", display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                {(week.results || (week.winners || []).map((w) => ({ winner: w, coaches: [w, ""], pts: [null, null] }))).map((r, ri) => {
                  const [c0, c1] = r.coaches || [];
                  const [pts0, pts1] = r.pts || [null, null];
                  const loser = r.winner === c0 ? c1 : c0;
                  const winnerPts = r.winner === c0 ? pts0 : pts1;
                  const loserPts = r.winner === c0 ? pts1 : pts0;
                  const tb = r.tiebreak_pts;
                  const tbWinner = tb && r.winner ? (r.winner === c0 ? tb[0] : tb[1]) : null;
                  const tbLoser = tb && r.winner ? (r.winner === c0 ? tb[1] : tb[0]) : null;
                  const hasDetails = c1 && c1 !== "BYE" && pts0 != null && pts1 != null;
                  return (
                    <div
                      key={ri}
                      style={{
                        fontSize: "0.78rem",
                        color: "#9fb7e3",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        {r.winner ? (
                          <>
                            <span style={{ color: "#a8fff0", fontWeight: 700 }}>{r.winner}</span>
                            {c1 && c1 !== "BYE" ? (
                              <>
                                {" def. "}<span>{loser}</span>
                                {winnerPts != null && (
                                  <span style={{ color: "#6884b7" }}>
                                    {" "}({Number(winnerPts).toFixed(2)} – {Number(loserPts).toFixed(2)})
                                  </span>
                                )}
                                {tb && <span style={{ color: "#ffe6a6", fontSize: "0.72rem" }}> TB: {Number(tbWinner).toFixed(2)}–{Number(tbLoser).toFixed(2)}</span>}
                              </>
                            ) : (
                              <span style={{ color: "#6884b7" }}> (bye)</span>
                            )}
                          </>
                        ) : (
                          <>
                            <span>{c0}</span> tied <span>{c1}</span>
                            {pts0 != null && <span style={{ color: "#6884b7" }}> ({Number(pts0).toFixed(2)} – {Number(pts1).toFixed(2)})</span>}
                          </>
                        )}
                      </span>
                      {hasDetails && (
                        <button
                          type="button"
                          onClick={() => openMatchupDetails(week.week, ri)}
                          style={{
                            background: "transparent",
                            border: "1px solid #3d5680",
                            color: "#a8fff0",
                            borderRadius: "4px",
                            padding: "0.1rem 0.45rem",
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            letterSpacing: "0.03em",
                          }}
                        >
                          Match details
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {matchupDetails && (
        <MatchupDetailsModal
          state={matchupDetails}
          loading={detailsLoading}
          error={detailsError}
          onClose={closeMatchupDetails}
        />
      )}
    </div>
  );
};

// ── Main Admin Dashboard ──────────────────────────────────────────────────────

const TABS = [
  "Game",
  "Upload Files",
  "Roster",
  "Users",
  "Results Override",
  "Swimmer Results",
  "Draft Setup",
];

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div style={adminPage} className="admin-page-shell">
      <h1 className="page-title">Admin Dashboard</h1>

      <div style={tabBar} className="admin-tab-bar">
        {TABS.map((label, i) => (
          <button
            key={label}
            aria-pressed={activeTab === i}
            onClick={() => setActiveTab(i)}
            style={activeTab === i ? { ...tabBtn, ...activeTabBtn } : tabBtn}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={tabContent} className="admin-tab-content">
        {activeTab === 0 && <GameTab />}
        {activeTab === 1 && <UploadTab />}
        {activeTab === 2 && <RosterTab />}
        {activeTab === 3 && <UsersTab />}
        {activeTab === 4 && <ResultsTab />}
        {activeTab === 5 && <SwimmerResultsTab />}
        {activeTab === 6 && <DraftTab />}
      </div>
    </div>
  );
};

export default AdminDashboard;

// ── Styles ────────────────────────────────────────────────────────────────────

const sectionTitle = {
  fontSize: "1.02rem",
  marginBottom: "0.38rem",
  color: "#eaf3ff",
};
const hint = { color: "#9fb7e3", fontSize: "0.82rem", marginBottom: "0.7rem" };
const card = {
  border: "1px solid rgba(86, 122, 198, 0.45)",
  borderRadius: "10px",
  padding: "0.8rem",
  background: "rgba(7, 20, 60, 0.86)",
};
const cardTitle = {
  marginBottom: "0.35rem",
  fontSize: "0.93rem",
  color: "#eaf3ff",
};
const fileInput = { padding: "0.3rem" };
const btn = {
  padding: "0.4rem 0.8rem",
  backgroundColor: "rgba(0,245,212,0.14)",
  color: "#a8fff0",
  border: "1px solid rgba(0,245,212,0.7)",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "0.78rem",
};
const resetBtn = {
  backgroundColor: "rgba(255, 126, 160, 0.14)",
  color: "#ffc1d3",
  borderColor: "rgba(255, 126, 160, 0.65)",
};
const lockBtn = {
  backgroundColor: "rgba(255, 196, 87, 0.14)",
  color: "#ffe6a6",
  borderColor: "rgba(255, 196, 87, 0.65)",
};
const unlockBtn = {
  backgroundColor: "rgba(126, 255, 190, 0.14)",
  color: "#bbffd6",
  borderColor: "rgba(126, 255, 190, 0.65)",
};
const secondaryBtn = {
  padding: "0.36rem 0.72rem",
  backgroundColor: "rgba(0,245,212,0.1)",
  color: "#a8fff0",
  border: "1px solid rgba(0,245,212,0.6)",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "0.76rem",
};
const statusHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "1rem",
  flexWrap: "wrap",
};
const statusGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: "0.55rem",
  marginTop: "0.65rem",
};
const statusCard = {
  padding: "0.62rem",
  borderRadius: "8px",
  border: "1px solid rgba(86, 122, 198, 0.4)",
  backgroundColor: "rgba(9, 23, 64, 0.72)",
  color: "#dbe9ff",
  display: "flex",
  flexDirection: "column",
  gap: "0.2rem",
};
const uploadedMeetsWrap = {
  marginTop: "0.9rem",
  borderTop: "1px solid rgba(86, 122, 198, 0.38)",
  paddingTop: "0.7rem",
};
const uploadedMeetsTitle = {
  margin: "0 0 0.45rem",
  fontSize: "0.86rem",
  color: "#eaf3ff",
};
const uploadedMeetsList = {
  display: "grid",
  gap: "0.4rem",
};
const uploadedMeetRow = {
  display: "grid",
  gridTemplateColumns: "28px 120px 1fr",
  gap: "0.55rem",
  alignItems: "center",
  border: "1px solid rgba(86, 122, 198, 0.36)",
  borderRadius: "8px",
  backgroundColor: "rgba(9, 23, 64, 0.62)",
  padding: "0.42rem 0.56rem",
};
const uploadedMeetCheckboxLabel = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
  borderRadius: "50%",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  cursor: "pointer",
  color: "#a8fff0",
  fontSize: "0.78rem",
  lineHeight: 1,
};
const uploadedMeetCheckbox = {
  position: "absolute",
  opacity: 0,
  width: "0",
  height: "0",
};
const uploadedMeetCheckboxText = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
};
const uploadedMeetDate = {
  color: "#cde2ff",
  fontWeight: 700,
  fontSize: "0.77rem",
};
const uploadedMeetTitle = {
  color: "#dbe9ff",
  fontSize: "0.78rem",
  fontWeight: 600,
};
const uploadedMeetEmpty = {
  margin: 0,
  color: "#9fb7e3",
  fontSize: "0.8rem",
  fontStyle: "italic",
};
const unassignedList = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "0.45rem",
  marginTop: "0.45rem",
};
const unassignedItem = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.65rem",
  border: "1px solid rgba(86, 122, 198, 0.4)",
  borderRadius: "8px",
  backgroundColor: "rgba(9, 23, 64, 0.72)",
  padding: "0.48rem 0.58rem",
};
const unassignedName = {
  color: "#d8e8ff",
  fontSize: "0.84rem",
  fontWeight: 600,
};
const unassignedActions = {
  display: "flex",
  alignItems: "center",
  gap: "0.45rem",
  flexWrap: "wrap",
  justifyContent: "flex-end",
};
const mutedText = {
  color: "#8ea7d8",
  fontStyle: "italic",
};
const smallBtn = {
  padding: "0.28rem 0.56rem",
  marginRight: "0.4rem",
  backgroundColor: "rgba(0,245,212,0.14)",
  color: "#a8fff0",
  border: "1px solid rgba(0,245,212,0.7)",
  borderRadius: "7px",
  cursor: "pointer",
  fontSize: "0.74rem",
};
const dangerBtn = {
  backgroundColor: "rgba(255, 126, 160, 0.14)",
  color: "#ffc1d3",
  borderColor: "rgba(255, 126, 160, 0.65)",
};
const table = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "0.75rem",
  fontSize: "0.82rem",
  backgroundColor: "rgba(9, 23, 64, 0.72)",
  borderRadius: "8px",
  overflow: "hidden",
};
const th = {
  borderBottom: "1px solid rgba(86, 122, 198, 0.45)",
  textAlign: "left",
  padding: "7px 9px",
  background: "#061642",
  color: "#8ea8d8",
  fontSize: "0.72rem",
  textTransform: "uppercase",
};
const td = {
  borderBottom: "1px solid rgba(86, 122, 198, 0.22)",
  padding: "6px 9px",
  verticalAlign: "middle",
  color: "#d8e8ff",
};
const rowEven = { background: "rgba(9, 23, 64, 0.72)" };
const rowOdd = { background: "rgba(5, 16, 48, 0.72)" };
const rowAdd = {
  background: "rgba(0,245,212,0.08)",
  borderTop: "1px dashed rgba(0,245,212,0.5)",
};
const inlineInput = {
  padding: "0.25rem 0.4rem",
  border: "1px solid rgba(98, 132, 212, 0.45)",
  borderRadius: "7px",
  fontSize: "0.8rem",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: "rgba(5, 17, 51, 0.85)",
  color: "#d8e8ff",
};
const checkboxGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "0.5rem",
};
const checkLabel = {
  display: "flex",
  alignItems: "center",
  gap: "0.45rem",
  fontSize: "0.82rem",
  color: "#d8e8ff",
};
const tabBar = {
  display: "flex",
  gap: "0.35rem",
  marginBottom: "0.75rem",
  flexWrap: "nowrap",
  overflowX: "auto",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  WebkitOverflowScrolling: "touch",
  padding: "0",
  borderRadius: "0",
  border: "none",
  backgroundColor: "transparent",
};
const tabBtn = {
  padding: "0.44rem 0.72rem",
  border: "none",
  borderRadius: "9px",
  background: "rgba(7, 20, 60, 0.45)",
  color: "#a6bce6",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "0.73rem",
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  boxShadow: "0 7px 18px rgba(0, 0, 0, 0.26)",
};
const activeTabBtn = {
  color: "#02061a",
  background: "#00f5d4",
  boxShadow: "0 0 14px rgba(0,245,212,0.3)",
};
const tabContent = {
  border: "1px solid rgba(0,245,212,0.34)",
  borderRadius: "12px",
  padding: "0.75rem",
  background: "rgba(7, 20, 60, 0.72)",
  boxShadow: "inset 0 0 0 1px rgba(0,245,212,0.08)",
};

const adminPage = {
  color: "#eaf3ff",
};

const matchupAdminRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.5rem",
  flexWrap: "wrap",
  padding: "0.5rem 0.6rem",
  borderRadius: "8px",
  background: "rgba(9,23,64,0.55)",
  border: "1px solid rgba(86,122,198,0.2)",
};

const matchupAdminLabel = {
  fontSize: "0.85rem",
  color: "#eaf3ff",
  flex: 1,
};


const coachScoreRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.5rem",
  padding: "0.5rem 0.65rem",
  borderRadius: "8px",
  background: "rgba(9,23,64,0.55)",
  border: "1px solid rgba(86,122,198,0.22)",
};

const coachScoreName = {
  fontSize: "0.88rem",
  fontWeight: 700,
  color: "#eaf3ff",
};

const coachScorePts = {
  fontSize: "0.88rem",
  fontWeight: 800,
  color: "#a8fff0",
};

const swimmerBlock = {
  borderBottom: "1px solid rgba(86, 122, 198, 0.2)",
  marginBottom: "0.2rem",
};
const swimmerBtn = {
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.5rem 0.6rem",
  background: "transparent",
  border: "none",
  color: "#d8e8ff",
  cursor: "pointer",
  fontSize: "0.85rem",
  fontWeight: "bold",
  textAlign: "left",
};
const eventBlock = {
  borderTop: "1px solid rgba(86, 122, 198, 0.15)",
  marginBottom: "0.15rem",
};
const eventBtn = {
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.35rem 0.5rem",
  background: "transparent",
  border: "none",
  color: "#a6bce6",
  cursor: "pointer",
  fontSize: "0.8rem",
  textAlign: "left",
};

